import { Link, useParams, Navigate } from "react-router-dom";
import SEO from "@/components/SEO";
import NewsletterSection from "@/components/NewsletterSection";
import HatchGrid from "@/components/HatchGrid";
import HatchCalendar from "@/components/HatchCalendar";
import { Reveal } from "@/components/Motion";
import { ARTICLES } from "@/data/articles";
import { ArrowLeft } from "lucide-react";

export default function JournalArticle() {
    const { slug } = useParams();
    const article = ARTICLES.find((a) => a.slug === slug);
    if (!article) return <Navigate to="/journal" replace />;

    return (
        <>
            <SEO title={article.title} description={article.description} />
            <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-40 pb-24" data-testid={`article-${article.slug}`}>
                <Reveal>
                    <Link to="/journal" data-testid="article-back-link" className="inline-flex items-center gap-2 font-sans text-sm text-slate-400 hover:text-teal transition-colors">
                        <ArrowLeft size={16} /> The Field Journal
                    </Link>
                    <div className="mt-8 flex items-center gap-3 font-mono text-[10px] tracking-[0.24em] uppercase">
                        <span className="text-teal">{article.category}</span>
                        <span className="text-slate-500">{article.date}</span>
                        <span className="text-slate-500">{article.readTime}</span>
                    </div>
                    <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-bold tracking-tight text-slate-50 leading-[1.08]">
                        {article.title}
                    </h1>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="relative mt-10 rounded-2xl overflow-hidden border border-white/10">
                        <img src={article.image} alt={article.imageAlt || article.title} className="w-full aspect-[16/8] object-cover" style={{ objectPosition: article.imagePosition || "50% 50%" }} />
                        <a
                            href={`https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(`https://troutstart.com/journal/${article.slug}`)}&media=${encodeURIComponent(`https://troutstart.com${article.image}`)}&description=${encodeURIComponent(article.title)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="pinterest-save-button"
                            className="absolute top-4 right-4 inline-flex items-center gap-2 rounded-full bg-[#0B1320]/80 backdrop-blur-md border border-white/15 px-4 py-2 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-200 hover:bg-[#E60023] hover:border-[#E60023] hover:text-white transition-colors duration-300"
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.739a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
                            </svg>
                            Save
                        </a>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <div className="mt-10 space-y-6">
                        {article.body.map((block, i) =>
                            block.calendar ? (
                                <div key={i} className="md:-mx-24 lg:-mx-40">
                                    <HatchCalendar rows={article.calendar} pin={{ url: `https://troutstart.com/journal/${article.slug}`, media: `https://troutstart.com${article.image}`, title: article.title }} />
                                </div>
                            ) : block.grid ? (
                                <div key={i} className="md:-mx-24 lg:-mx-40">
                                    <HatchGrid rows={article.grid} pin={{ url: `https://troutstart.com/journal/${article.slug}`, media: `https://troutstart.com${article.image}`, title: article.title }} />
                                </div>
                            ) : block.img ? (
                                <figure key={i} className="rounded-2xl overflow-hidden border border-white/10">
                                    <img src={block.img} alt={block.caption || article.title} loading="lazy" className="w-full max-h-[480px] object-cover" style={{ objectPosition: block.imgPosition || "50% 50%" }} />
                                    {block.caption && (
                                        <figcaption className="px-5 py-3 font-mono text-xs text-slate-500 bg-[#111C2E]">{block.caption}</figcaption>
                                    )}
                                </figure>
                            ) : block.h ? (
                                <h2 key={i} className="pt-4 font-sans text-xl sm:text-2xl font-medium text-slate-100">
                                    {block.h}
                                </h2>
                            ) : (
                                <p key={i} className="font-body text-base leading-relaxed text-slate-300">
                                    {block.link && block.p.includes(block.link.text) ? (
                                        <>
                                            {block.p.slice(0, block.p.indexOf(block.link.text))}
                                            <Link to={block.link.href} className="text-teal underline decoration-teal/40 underline-offset-4 hover:text-coral hover:decoration-coral/50 transition-colors">
                                                {block.link.text}
                                            </Link>
                                            {block.p.slice(block.p.indexOf(block.link.text) + block.link.text.length)}
                                        </>
                                    ) : (
                                        block.p
                                    )}
                                </p>
                            ),
                        )}
                    </div>
                    <div className="mt-12 rounded-2xl border border-teal/25 bg-teal/5 p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                        <p className="font-sans text-base text-slate-200">Keep the momentum going:</p>
                        <Link
                            to={article.cta.href}
                            data-testid="article-cta-link"
                            className="rounded-full bg-teal px-6 py-3 font-sans text-sm font-semibold text-[#0B1320] hover:bg-coral hover:text-white transition-colors duration-300 shrink-0"
                        >
                            {article.cta.label}
                        </Link>
                    </div>
                </Reveal>
            </article>
            <NewsletterSection />
        </>
    );
}
